/**
 * Lumina Wishlist — localStorage-based wishlist
 * v2 — unified item storage [{id, handle}]
 * Depends on: lumina-utils.js (window.Lumina.toast)
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'lumina_wishlist';

  var Wishlist = {
    /* ── Storage helpers ─────────────────────────────── */

    /** Returns [{id: String, handle: String|null}, ...] */
    get: function () {
      try {
        var raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        if (!Array.isArray(raw)) return [];

        /* Migrate old format: array of plain strings (IDs only) */
        if (raw.length > 0 && typeof raw[0] === 'string') {
          /* Try to recover handles from the old handles key */
          var oldHandles = [];
          try { oldHandles = JSON.parse(localStorage.getItem('lumina_wishlist_handles') || '[]'); } catch (e) {}
          raw = raw.map(function (id, i) {
            return { id: String(id), handle: oldHandles[i] || null };
          });
          this._save(raw); /* persist migrated format */
        }

        var map = {};
        var deduplicated = [];
        raw.forEach(function (item) {
          var idStr = String(item.id);
          if (!map[idStr]) {
            map[idStr] = true;
            deduplicated.push({ id: idStr, handle: item.handle || null });
          } else if (item.handle) {
            var existing = deduplicated.find(function(x) { return x.id === idStr; });
            if (existing && !existing.handle) existing.handle = item.handle;
          }
        });
        return deduplicated;
      } catch (e) { return []; }
    },

    _save: function (items) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (e) {}
    },

    getIds: function () {
      return this.get().map(function (item) { return item.id; });
    },

    getHandles: function () {
      return this.get().map(function (item) { return item.handle; }).filter(Boolean);
    },

    /* ── Public API ──────────────────────────────────── */

    init: function () {
      this.sync();
      this.observeDOM();
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-wishlist-toggle]');
        if (!btn) return;

        var id = String(btn.dataset.wishlistToggle);

        /* Resolve handle — look on the button itself first, then traverse up */
        var handle = btn.dataset.productHandle || null;
        if (!handle) {
          var card = btn.closest('[data-product-handle]');
          if (card) handle = card.dataset.productHandle || null;
        }

        Wishlist.toggle(id, btn, handle);
      });
    },

    toggle: function (id, btn, handle) {
      if (!id) return;
      var strId = String(id);
      var items = this.get();
      var idx   = items.findIndex(function (item) { return item.id === strId; });
      var toast = window.Lumina && window.Lumina.toast;

      if (idx === -1) {
        items.push({ id: strId, handle: handle });
        btn && btn.classList.add('is-wishlisted');
        if (btn) btn.setAttribute('aria-label', btn.getAttribute('aria-label') ? btn.getAttribute('aria-label').replace('Add', 'Remove') : 'Remove from wishlist');
        toast && toast.show('Added to wishlist \u2661', 'success');
      } else {
        /* Preserve handle if the new call provides null (e.g. called from page) */
        if (handle && !items[idx].handle) items[idx].handle = handle;
        items.splice(idx, 1);
        btn && btn.classList.remove('is-wishlisted');
        toast && toast.show('Removed from wishlist', 'info');
      }

      this._save(items);
      this.sync();
    },

    remove: function (id) {
      var items = this.get().filter(function (item) { return item.id !== id; });
      this._save(items);
      this.sync();
    },

    clear: function () {
      this._save([]);
      /* Remove old handle key if present */
      try { localStorage.removeItem('lumina_wishlist_handles'); } catch (e) {}
      this.sync();
    },

    sync: function () {
      var ids     = this.getIds();
      var countEl = document.querySelector('[data-wishlist-count-display]');

      if (countEl) {
        countEl.textContent = ids.length;
        countEl.hidden = ids.length === 0;
      }

      document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
        var id = String(btn.dataset.wishlistToggle);
        if (ids.indexOf(id) !== -1) {
          btn.classList.add('is-wishlisted');
          if (btn.getAttribute('aria-label') && btn.getAttribute('aria-label').indexOf('Add') !== -1) {
             btn.setAttribute('aria-label', btn.getAttribute('aria-label').replace('Add', 'Remove'));
          }
        } else {
          btn.classList.remove('is-wishlisted');
        }
      });
    },

    observeDOM: function() {
      // Automatically sync newly added DOM nodes (like upsells or infinite scroll)
      var utils    = window.Lumina && window.Lumina.utils;
      var debounce = utils && utils.debounce;
      var cb = debounce ? debounce(function () { Wishlist.sync(); }, 100) : function () { Wishlist.sync(); };
      var observer = new MutationObserver(cb);
      observer.observe(document.body, { childList: true, subtree: true });
    }
  };

  window.Lumina = window.Lumina || {};
  window.Lumina.wishlist = Wishlist;

})();
